import React, { useEffect, useState } from "react";
import { useBrandStore } from "../store/brandStore";
import {
  Button,
  Table,
  Card,
  Row,
  Col,
  Modal,
  Divider,
  Dropdown,
  MenuProps,
  Tag,
} from "antd";
import {
  PlusCircleOutlined,
  WarningFilled,
  EllipsisOutlined,
  ThunderboltOutlined,
  CloseCircleTwoTone,
  CheckCircleTwoTone,
  SettingOutlined,
} from "@ant-design/icons";
import CustomModal from "../components/common/Modal";
import { useLocation, useNavigate } from "react-router-dom";
import { RoutePaths } from "../routes/route";
import { brandRFMProfileService } from "../services/brandRFMProfileService";
import { useBrandRFMProfileStore } from "../store/brandRFMProfileStore";

const RFMTemplate: React.FC = () => {
  // =====================
  // State Declarations
  // =====================`

  // Hooks & Store
  const { brandId } = useBrandStore();
  // UI & State Management
  const [loading, setLoading] = useState(false);
  const [listProfile, setListProfile] = useState<any>([]);
  const [defaultSetting, setDefaultSetting] = useState<any>([]);

  const {
    brandRFMProfile,
    selectedBrandRFMProfile,
    setBrandRFMProfile,
    setSelectedBrandRFMProfile,
  } = useBrandRFMProfileStore();

  // Modals & Notifications
  const [deleteModalDetailVisible, setDeleteModalDetailVisible] =
    useState(false);
  const [activateModalDetailVisible, setActivateModalDetailVisible] =
    useState(false);
  const [isDefaultWarningVisible, setIsDefaultWarningVisible] = useState(false);
  const [isModalSuccessVisible, setIsModalSuccessVisible] = useState(false);
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);
  const [isErrorDefaultModalVisible, setIsErrorDefaultModalVisible] =
    useState(false);
  const [titleModalSuccessVisible, setTitleModalSuccessVisible] = useState("");
  const [messageModalSuccessVisible, setMessageModalSuccessVisible] =
    useState("");

  // Responsive
  const isMobileOrTablet = window.innerWidth <= 1024;

  // =====================
  // Function handler
  // =====================
  const handleSetAsDefault = async () => {
    if (selectedBrandRFMProfile) {
      try {
        setLoading(true);
        await brandRFMProfileService.setDefaultBrandRFMProfile(
          selectedBrandRFMProfile
        );
        fetchBrandRFMProfile();
        setTitleModalSuccessVisible("Default Template Updated");
        setMessageModalSuccessVisible(
          "You've successfully set this as your default template."
        );
        setIsDefaultWarningVisible(false);
        setIsModalSuccessVisible(true);
      } catch {
        console.error("Error delete profile!");
        setIsErrorModalVisible(true);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCancelChanges = () => {
    setIsDefaultWarningVisible(false);
  };

  const confirmDeleteBrandRFMProfile = async () => {
    if (selectedBrandRFMProfile && brandId) {
      try {
        setLoading(true);
        await brandRFMProfileService.deleteBrandRFMProfile(
          selectedBrandRFMProfile?.Id
        );
        await fetchBrandRFMProfile();

        setDeleteModalDetailVisible(false);
        setTitleModalSuccessVisible("Deleted Success");
        setMessageModalSuccessVisible(
          "Your template has been deleted successfully"
        );
        setIsDefaultWarningVisible(false);
        setIsModalSuccessVisible(true);
      } catch {
        console.error("Error delete profile!");
        setIsErrorModalVisible(true);
      } finally {
        setLoading(false);
      }
    }
  };

  const confirmActivateModalDetailVisible = async () => {
    if (selectedBrandRFMProfile && brandId) {
      try {
        setLoading(true);
        await brandRFMProfileService.activateBrandRFMProfile(
          selectedBrandRFMProfile?.Id
        );
        await fetchBrandRFMProfile();

        setActivateModalDetailVisible(false);
        setTitleModalSuccessVisible("Activate Success");
        setMessageModalSuccessVisible(
          "Your profile has been activate successfully"
        );
        setIsDefaultWarningVisible(false);
        setIsModalSuccessVisible(true);
      } catch {
        console.error("Error delete profile!");
        setIsErrorModalVisible(true);
      } finally {
        setLoading(false);
      }
    }
  };

  // =====================
  // Data Fetching
  // =====================
  const fetchBrandRFMProfile = async () => {
    if (brandId) {
      setLoading(true);
      try {
        const data = await brandRFMProfileService.getBrandRFMProfile(brandId);
        setBrandRFMProfile(Array.isArray(data) ? data : []);
        setListProfile(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const generateDefaultProfiles = async (setting: any) => {
    const profile1 = await makeDefaultProfile(setting, {
      Id: 0,
      Name: "Full Service Default Profiles",
      Description: "Full Service Default Profiles",
    });

    const profile2 = await makeDefaultProfile(setting, {
      Id: -1,
      Name: "Quick Service Default Profiles",
      Description: "Quick Service Default Profiles",
    });

    return [profile1, profile2];
  };

  // =====================
  // Useeffect
  // =====================
  useEffect(() => {
    const timeout = setTimeout(async () => {
      if (brandId) {
        await fetchBrandRFMProfile();
        // const fetchDefaultSetting = await defaultSettings(brandId);
        // setDefaultSetting(fetchDefaultSetting);

        // const defaultProfiles = await generateDefaultProfiles(
        //   fetchDefaultSetting
        // );
        // setListProfile((prev: any) => [...defaultProfiles, ...prev]);

        // setBrandRFMProfile((prev: any) => [...prev, profile1, profile2]);
      }
    }, 100);
    const fetchSettings = async () => {};
    fetchSettings();

    return () => clearTimeout(timeout);
  }, [brandId]);

  useEffect(() => {
    document.body.style.overflowY = "hidden";
    window.scrollTo(0, 0);

    return () => {
      document.body.style.overflowY = "";
    };
  }, []);

  // =====================
  // Columns
  // =====================

  const segmentColumns = [
    {
      title: "No ",
      dataIndex: "No",
      key: "No",
      align: "center" as const,
      width: "5%",
      render: (_: any, record: any, index: number) => index + 1,
    },
    {
      title: "Profile Name",
      dataIndex: "Name",
      key: "Name",
      width: "25%",
      render: (_: any, record: any) => (
        <div style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
          {record.Name}
        </div>
      ),
    },
    {
      title: "Description",
      dataIndex: "Description",
      key: "Description",
      width: "40%",
      render: (_: any, record: any) => (
        <div style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
          {record.Description || "No description"}
        </div>
      ),
    },
    {
      title: "Active Profile",
      dataIndex: "IsActive",
      key: "IsActive",
      align: "center" as const,
      width: "10%",
      render: (value: boolean, record: any) =>
        value ? (
          <Tag color="green" style={{ fontSize: 14 }}>
            Active
          </Tag>
        ) : (
          <Button
            type="primary"
            icon={<SettingOutlined />}
            onClick={() => {
              setSelectedBrandRFMProfile(record);
              const searchParams = new URLSearchParams(location.search);
              searchParams.delete("profileId");
              setActivateModalDetailVisible(true);
            }}
          >
            Set As Active
          </Button>
        ),
    },
    {
      title: "Action",
      key: "Action",
      width: "15%",
      render: (_: any, record: any) => {
        const items: MenuProps["items"] = [
          {
            key: `view-${record.Id}`,
            label: "View Detail",
            onClick: () => {
              setSelectedBrandRFMProfile(record);
              const searchParams = new URLSearchParams(location.search);
              searchParams.delete("profileId");
              navigate(
                `${
                  RoutePaths.IFRAMEVIEWRFMPROFILE
                }?${searchParams.toString()}&profileId=${record.Id}`
              );
            },
          },
          {
            key: `edit-${record.Id}`,
            label: "Edit Profile",
            onClick: () => {
              setSelectedBrandRFMProfile(record);
              const searchParams = new URLSearchParams(location.search);
              searchParams.delete("profileId");
              navigate(
                `${
                  RoutePaths.IFRAMEEDITRFMPROFILE
                }?${searchParams.toString()}&profileId=${record.Id}`
              );
            },
          },
        ];
        if (
          record.Name !== "Full Service Default Profiles" &&
          record.Name !== "Quick Service Default Profiles" &&
          !record.IsActive
        ) {
          items.push({
            key: `delete-${record.Id}`,
            label: "Delete Profile",
            onClick: () => {
              setSelectedBrandRFMProfile(record);
              setDeleteModalDetailVisible(true);
            },
          });
        }

        return (
          <Dropdown
            menu={{ items }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button icon={<EllipsisOutlined />} type="text" />
          </Dropdown>
        );
      },
    },
  ];

  // =====================
  // Modal
  // =====================

  const closeErrorModal = () => {
    setIsErrorModalVisible(false);
  };
  const closeErrorDefaultModal = () => {
    setIsErrorDefaultModalVisible(false);
  };
  const navigate = useNavigate();
  const location = useLocation();

  const isDisabled = brandRFMProfile.length >= 10;

  const makeDefaultProfile = async (
    brandRFMProfileSetting: any[],
    overrides: Partial<{
      Id: number;
      Name: string;
      Description: string;
    }> = {}
  ) => {
    let settings = Array.isArray(brandRFMProfileSetting)
      ? brandRFMProfileSetting
      : [];
    if (overrides.Id === -1) {
      let prevEnd = -0.01;

      settings = settings.map((item) => {
        if (item.RFMType === 3) {
          const scaledEnd = item.RFMEndValue > 0 ? item.RFMEndValue * 3 : 0;

          const newStart = parseFloat((prevEnd + 0.01).toFixed(2));

          prevEnd = scaledEnd;

          return {
            ...item,
            RFMStartValue: newStart,
            RFMEndValue: scaledEnd,
          };
        }
        return item;
      });
    }

    return {
      Id: overrides.Id ?? 0,
      BrandId: brandId,
      BrandRFMProfileSetting: settings,
      Name: overrides.Name ?? "",
      Description: overrides.Description ?? "",
      IsDefault: false,
      CreatedDate: new Date(),
    };
  };

  return (
    <>
      <div style={{ height: "85vh", overflowY: "auto" }}>
        <Card
          className="card-member-rfm-segment"
          variant="borderless"
          style={{ width: "100%", position: "relative" }}
          extra={""}
        >
          <div
            style={{
              position: "sticky",
              top: 0,
              background: "white",
              zIndex: 1000,
              padding: "12px 24px 0px 24px",
            }}
          >
            <Row
              align={"middle"}
              justify={isMobileOrTablet ? "center" : "start"}
            >
              <Col
                xs={12}
                md={12}
                xl={12}
                lg={12}
                style={{
                  fontSize: 20,
                  textAlign: isMobileOrTablet ? "left" : "left",
                }}
              >
                <span style={{ fontWeight: "bold" }}>RFM Profile Settings</span>
              </Col>
              <Col
                xs={12}
                md={12}
                xl={12}
                lg={12}
                style={{
                  marginTop: 10,
                  textAlign: isMobileOrTablet ? "center" : "left",
                }}
              >
                <Row justify={"end"} gutter={[8, 0]} style={{ gap: 8 }}>
                  <Col
                    span={24}
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 8,
                    }}
                  >
                    <Button
                      type="primary"
                      size={isMobileOrTablet ? "middle" : "large"}
                      block
                      disabled={isDisabled}
                      style={{
                        backgroundColor: isDisabled ? "#B0BEC5" : "#1565C0",
                        color: isDisabled ? "#ECEFF1" : "white",
                        opacity: isDisabled ? 0.6 : 1,
                        fontWeight: 700,
                        fontSize: isMobileOrTablet ? 12 : 14,
                        width: 210,
                        gap: 4,
                        cursor: isDisabled ? "not-allowed" : "pointer",
                      }}
                      onClick={() =>
                        navigate(
                          `${RoutePaths.IFRAMECREATERFMPROFILE}${location.search}`
                        )
                      }
                    >
                      <PlusCircleOutlined />
                      <span
                        style={{
                          fontSize: isMobileOrTablet ? 12 : 16,
                        }}
                      >
                        {" "}
                        Add New Profile
                      </span>
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Divider style={{ margin: "12px 0px" }} />
          </div>
          <div className="content-member-rfm-segment">
            <Table
              style={{ width: "100%" }}
              columns={segmentColumns}
              dataSource={listProfile}
              rowKey="Id"
              scroll={{ x: "max-content" }}
              rowClassName={"segment-cell"}
              loading={loading}
              pagination={false}
              locale={{ emptyText: "No Profile yet" }}
            />

            <Row
              justify="center"
              align="middle"
              gutter={[10, 10]}
              wrap
              style={{ marginTop: 25 }}
            >
              <Col xs={24} md={24} xl={12}></Col>
            </Row>
          </div>
        </Card>
        <CustomModal
          type="confirm"
          title="Delete Profile"
          message="Are you sure you want to delete this profile?"
          isVisible={deleteModalDetailVisible}
          onClose={() => setDeleteModalDetailVisible(false)}
          onConfirm={confirmDeleteBrandRFMProfile}
        />
        <CustomModal
          type="activate"
          title="Activate Profile"
          message="Are you sure you want to activate this profile?"
          isVisible={activateModalDetailVisible}
          onClose={() => setActivateModalDetailVisible(false)}
          onCancelClick={() => setActivateModalDetailVisible(false)}
          onConfirm={confirmActivateModalDetailVisible}
          onSuccessClick={confirmActivateModalDetailVisible}
          successButtonText={"Activate"}
          cancelButtonText={"Cancel"}
        />
        <CustomModal
          type="error"
          title="Operation Failed"
          message="Something went wrong. Please try again."
          isVisible={isErrorModalVisible}
          onClose={closeErrorModal}
          autoClose
          duration={3000}
        />
        <Modal
          title=""
          open={isDefaultWarningVisible}
          width={550}
          maskClosable={false}
          closable={false}
          footer={
            <Row gutter={16}>
              <Col span={12}>
                <Button
                  key="cancel"
                  className="w-100"
                  style={{ height: 45 }}
                  onClick={() => handleCancelChanges()}
                >
                  Cancel
                </Button>
              </Col>
              <Col span={12}>
                <Button
                  key="edit"
                  type="primary"
                  style={{
                    backgroundColor: "#1565C0",
                    color: "white",
                    height: 45,
                  }}
                  onClick={() => handleSetAsDefault()}
                  className="w-100"
                >
                  Set as Default
                </Button>
              </Col>
            </Row>
          }
        >
          <Row justify={"center"} style={{ marginTop: 25 }}>
            <WarningFilled style={{ color: "#EEB711", fontSize: 100 }} />{" "}
          </Row>
          <Row justify={"center"} style={{ fontSize: 16, fontWeight: "bold" }}>
            Set as Default Template
          </Row>

          <Row justify={"center"}>
            <span style={{ color: "#6c757d", fontSize: "14px" }}>
              Are you sure you want to set this template as your default?
            </span>
            <span style={{ color: "#6c757d", fontSize: "14px" }}>
              {" "}
              This will replace your current default template
            </span>
          </Row>
          <Divider />
        </Modal>
        <CustomModal
          type="success"
          title={titleModalSuccessVisible}
          message={messageModalSuccessVisible}
          isVisible={isModalSuccessVisible}
          onClose={() => setIsModalSuccessVisible(false)}
          autoClose
          duration={3000}
        />
        <CustomModal
          type="error"
          title="Operation Failed"
          message="You cannot delete the default profile. Please set another profile as default before deleting this one."
          isVisible={isErrorDefaultModalVisible}
          onClose={closeErrorDefaultModal}
          autoClose
          duration={3000}
        />
      </div>
    </>
  );
};

export default RFMTemplate;
